"use client"

import { notify } from "@/lib/notifications/notify"
import { SendMark } from "@/components/ui/send-mark"
import { useEffect, useRef, useState } from "react"
import { Loader2, Check } from "lucide-react"
import { toast } from "sonner"
import { createClient } from "@/lib/supabase/client"
import { Textarea } from "@/components/ui/textarea"
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet"

interface BoothPlan {
  id: string
  name: string | null
  description: string | null
  price: number | null
  billing_period: string | null
  sessions: number | null
  is_enabled: boolean
}

interface OwnerShop {
  id: string
  name: string
  city: string | null
  state: string | null
}

interface InquireModalProps {
  open: boolean
  onClose: () => void
  barberId: string
  barberName: string
  ownerId: string | null
  ownerShop: OwnerShop | null
  onSent?: () => void
}

function defaultMessage(barberName: string, shop: OwnerShop | null) {
  const where = shop
    ? `${shop.name}${shop.city ? ` in ${[shop.city, shop.state].filter(Boolean).join(", ")}` : ""}`
    : "our shop"
  return `Hey ${barberName}, I came across your work on BOOTH Hiring and I'd love to have you join us at ${where}. Open to chatting?`
}

function planLabel(p: BoothPlan) {
  const cadence = p.billing_period === "weekly" ? "wk"
    : p.billing_period === "biweekly" ? "2wk"
    : p.billing_period === "daily" ? "day"
    : "mo"
  const name = p.name || p.description || "Plan"
  return `${name} · $${p.price ?? 0}/${cadence}`
}

export function InquireModal({ open, onClose, barberId, barberName, ownerId, ownerShop, onSent }: InquireModalProps) {
  const supabase = createClient()
  const [message, setMessage] = useState("")
  const [plans, setPlans] = useState<BoothPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  // Synchronous guard against rapid-click double-sends. The `sending` state
  // updates async — between two clicks fired in the same tick, React hasn't
  // re-rendered yet so the button's disabled prop is stale. A ref blocks
  // re-entry immediately.
  const sendingRef = useRef(false)

  // Reset state + reload plans whenever the modal opens for a new barber
  useEffect(() => {
    if (!open) return
    setMessage(defaultMessage(barberName, ownerShop))
    setSelectedPlanId(null)
    if (!ownerShop?.id) { setPlans([]); return }
    let cancelled = false
    setPlansLoading(true)
    supabase
      .from("booth_plans")
      .select("id, name, description, price, billing_period, sessions, is_enabled")
      .eq("shop_id", ownerShop.id)
      .eq("is_enabled", true)
      .then(({ data }: { data: BoothPlan[] | null }) => {
        if (cancelled) return
        const sorted = ((data as BoothPlan[]) || []).sort((a, b) => (a.price || 0) - (b.price || 0))
        setPlans(sorted)
        setPlansLoading(false)
      })
    return () => { cancelled = true }
  }, [open, barberName, ownerShop?.id])

  const send = async () => {
    if (sendingRef.current) return
    if (!ownerId) { toast.error("You need to be signed in"); return }
    if (!message.trim()) { toast.error("Add a message"); return }
    sendingRef.current = true
    setSending(true)
    try {
      // The card was drawn from a list fetched a while ago. Check the switch
      // now: a barber who turned "Open to work" off since then is not taking
      // outreach, and the invite should not go out.
      const { data: target } = await supabase
        .from("barber_profiles")
        .select("is_open_to_work")
        .eq("id", barberId)
        .maybeSingle()
      if (target && target.is_open_to_work === false) {
        toast.error(`${barberName || "This barber"} isn't open to outreach right now`)
        onClose()
        return
      }

      const attachedPlan = plans.find(p => p.id === selectedPlanId) || null
      const planSummary = attachedPlan ? planLabel(attachedPlan) : null

      // 1. Get-or-create conversation between owner and barber.
      // The conversations table uses participant_1 / participant_2. Nothing
      // in the database stops a pair having two rows, and maybeSingle() errors
      // on two, which failed the whole invite. Take the oldest thread.
      const { data: convo, error: convoSelErr } = await supabase
        .from("conversations")
        .select("id")
        .or(`and(participant_1.eq.${ownerId},participant_2.eq.${barberId}),and(participant_1.eq.${barberId},participant_2.eq.${ownerId})`)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle()
      if (convoSelErr) throw convoSelErr

      let conversationId = convo?.id
      if (!conversationId) {
        const { data: newConvo, error: newConvoErr } = await supabase
          .from("conversations")
          .insert({ participant_1: ownerId, participant_2: barberId })
          .select("id")
          .single()
        if (newConvoErr) throw newConvoErr
        conversationId = newConvo?.id
      }

      // 2. The actual message — body + (optional) plan suggestion line
      if (conversationId) {
        const body = planSummary
          ? `${message.trim()}\n\nSuggested plan: ${planSummary}`
          : message.trim()
        const { error: msgErr } = await supabase.from("messages").insert({
          conversation_id: conversationId,
          sender_id: ownerId,
          receiver_id: barberId,
          content: body,
        })
        if (msgErr) throw msgErr
      }

      // 3. Notification to the barber. After the message, so a send the
      // database refuses (a block) does not leave a notification pointing at
      // a message that was never written.
      const { error: notifErr } = await notify({
        recipient: barberId,
        type: "invite_to_apply",
        shopId: ownerShop?.id ?? null,
        // The owner's own line about the shop. Kept in metadata by the server,
        // so the app shows it and the lock screen does not.
        note: message.trim(),
        metadata: {
          shop_id: ownerShop?.id ?? null,
          owner_id: ownerId,
          plan_id: attachedPlan?.id ?? null,
          plan_summary: planSummary,
        },
      })
      // The message is already in their inbox, so a failed ping is not a
      // failed invite.
      if (notifErr) console.warn("[inquire] notification failed", notifErr)

      toast.success("Invite sent")
      onSent?.()
      onClose()
    } catch (err: any) {
      // 42501 here is the block policy (1033): one of the two has blocked
      // the other. Say that instead of the raw row-level-security text.
      toast.error(
        err?.code === "42501"
          ? "You can't message this barber"
          : err?.message || "Couldn't send invite",
      )
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  return (
    <BottomSheet open={open} onOpenChange={(o) => { if (!o && !sending) onClose() }}>
      <BottomSheetContent closeDisabled={sending}>
        <div className="px-6 pb-5 pt-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40">Invite</p>
          <BottomSheetTitle className="mt-1.5 pr-8 text-xl font-medium leading-snug tracking-tight">
            {barberName}
          </BottomSheetTitle>
          <BottomSheetDescription className="mt-1 text-sm leading-relaxed text-white/50">
            Send a personal note. Attach a booth plan so they see your offer up front.
          </BottomSheetDescription>
        </div>

        <div className="space-y-5 px-6">
          <div>
            <label htmlFor="inquire-message" className="sr-only">Your message</label>
            <Textarea
              id="inquire-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={1000}
              disabled={sending}
              className="resize-none rounded-2xl border-white/10 bg-white/[0.04] text-sm leading-relaxed text-white placeholder:text-white/35 focus-visible:border-white/25 focus-visible:ring-1 focus-visible:ring-white/20"
              placeholder="Hey, I came across your work and I'd love to have you join us…"
            />
          </div>

          <div className="space-y-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40">
              Booth plan <span className="font-normal normal-case tracking-normal text-white/30">(optional)</span>
            </p>
            {plansLoading ? (
              <div className="flex items-center gap-2 text-xs text-white/40">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading plans…
              </div>
            ) : plans.length === 0 ? (
              <p className="text-xs text-white/40">No active booth plans on this shop.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {plans.map((p) => {
                  const selected = selectedPlanId === p.id
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPlanId(selected ? null : p.id)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                        selected
                          ? "border-white bg-white text-black"
                          : "border-white/15 bg-white/[0.04] text-white/70 hover:border-white/40 hover:text-white"
                      }`}
                    >
                      {selected && <Check className="h-3 w-3" />}
                      {planLabel(p)}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Single full-width commit at the bottom edge — the one thing this
            sheet exists to do. Cancel is the X and the backdrop. */}
        <div className="px-6 pb-6 pt-5">
          <button
            type="button"
            onClick={send}
            disabled={sending || !message.trim()}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white text-sm font-semibold tracking-wide text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendMark size={16} />}
            Send invite
          </button>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  )
}
